import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "@/i18n";
import { API, PORTS, COMPANY } from "@/data";
import { Send } from "lucide-react";

const initial = { name: "", company: "", email: "", phone: "", origin: "montreal", destination: "abidjan", container_type: "40' Dry", quantity: 1, message: "" };

export const QuoteForm = () => {
  const { lang, t } = useLang();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, lang, quantity: Number(form.quantity) || 1 });
      toast.success(t("quote.success"));
      setForm(initial);
    } catch {
      toast.error(t("quote.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="py-32 bg-surface/40 border-y border-white/10" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <Overline>{t("quote.overline")}</Overline>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t("quote.title")}</h2>
            <p className="mt-6 text-steel-400 leading-relaxed">{t("quote.sub")}</p>
            <div className="mt-12 border-l-2 border-ember pl-6">
              <p className="font-display text-xl font-semibold">{COMPANY.phone}</p>
              <p className="text-sm text-steel-500 mt-1 break-all">{COMPANY.quoteEmail}</p>
              <p className="text-sm text-steel-500">{COMPANY.addressLine1}, {COMPANY.addressLine2}</p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} className="bg-surface border border-white/10 p-8 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="quote-form">
              <input required placeholder={t("quote.name")} value={form.name} onChange={set("name")} className="input-tactical" data-testid="quote-name-input" />
              <input placeholder={t("quote.company")} value={form.company} onChange={set("company")} className="input-tactical" data-testid="quote-company-input" />
              <input required type="email" placeholder={t("quote.email")} value={form.email} onChange={set("email")} className="input-tactical" data-testid="quote-email-input" />
              <input placeholder={t("quote.phone")} value={form.phone} onChange={set("phone")} className="input-tactical" data-testid="quote-phone-input" />
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-steel-500 mb-2">{t("quote.origin")}</label>
                <select value={form.origin} onChange={set("origin")} className="input-tactical" data-testid="quote-origin-select">
                  <option value="montreal">Montréal</option>
                  <option value="toronto">Toronto</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-steel-500 mb-2">{t("quote.destination")}</label>
                <select value={form.destination} onChange={set("destination")} className="input-tactical" data-testid="quote-destination-select">
                  {PORTS.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.country[lang]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-steel-500 mb-2">{t("quote.container")}</label>
                <select value={form.container_type} onChange={set("container_type")} className="input-tactical" data-testid="quote-container-select">
                  {t("quote.containers").map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-steel-500 mb-2">{t("quote.quantity")}</label>
                <input type="number" min="1" value={form.quantity} onChange={set("quantity")} className="input-tactical" data-testid="quote-quantity-input" />
              </div>
              <textarea placeholder={t("quote.message")} value={form.message} onChange={set("message")} rows={4}
                className="input-tactical sm:col-span-2 resize-none" data-testid="quote-message-input" />
              <button type="submit" disabled={sending} data-testid="quote-submit-btn"
                className="sm:col-span-2 bg-ember hover:bg-emberdark disabled:opacity-50 text-white font-semibold px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2">
                <Send size={16} /> {sending ? t("quote.sending") : t("quote.submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
